import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  InputText,
  SidePanel,
  SidePanelLoader,
  SidePanelType,
  ButtonPrimary,
  ButtonPseudo,
  Checkbox,
  getResponseErrors,
  ResponseError,
  responseErrorsResolver,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  useLazyGetCountryQuery,
  useUpdateCountryRiskScoreMutation,
  useUpdateCountryActivityMutation,
} from '../store/services/countries';

import ROUTES from '../../../routes';

import cn from '../styles.module.css';

const CountryPanel = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [riskScoreValue, setRiskScoreValue] = useState('');
  const [active, setActive] = useState(false);
  const [errorList, setErrorList] = useState('');

  const { country } = useAppSelector((state) => state.settings.countries);

  const navigate = useNavigate();
  const { id } = useParams();

  const [getCountry, { isLoading, isFetching }] = useLazyGetCountryQuery();
  const [updateCountryRiskScore, { isLoading: requestInProgress }] =
    useUpdateCountryRiskScoreMutation();
  const [updateCountryActivity, { isLoading: activeInProgress }] =
    useUpdateCountryActivityMutation();

  const handleClose = () => {
    setPanelIsOpen(false);
    setErrorList('');

    setTimeout(() => {
      navigate(ROUTES.COUNTRIES);
    }, 100);
  };

  const updateCountryRiskScoreAction = async () => {
    const response = (await updateCountryRiskScore({
      id: id as string,
      body: {
        riskScore: Number(riskScoreValue),
      },
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError: setErrorList,
      });
    }

    if (active !== country?.active) {
      const activeResponse = (await updateCountryActivity({
        id: id as string,
        body: {
          active,
        },
      })) as { error?: ResponseError };

      if (activeResponse?.error) {
        responseErrorsResolver({
          errors: activeResponse?.error,
          setError: () => {},
          setGlobalError: setErrorList,
        });
      }
    }
  };

  const getCountryAction = useCallback(async (countryId: string) => {
    const response = (await getCountry(countryId)) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({
        error: response.error,
        setErrorList,
      });
    }
  }, []);

  useEffect(() => {
    if (country) {
      setRiskScoreValue(country?.riskScore?.toString() || '');
      setActive(country?.active);
    }
  }, [country]);

  useEffect(() => {
    if (id) {
      getCountryAction(id);
    }
  }, [id]);

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  let content;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <>
        <div className={cn.countryFormGroup}>
          <Checkbox
            name="active"
            checked={active}
            type="checkbox"
            onChange={(e) => setActive(e.target.checked)}
            title="Active"
          />
        </div>
        <div className={cn.countryFormGroup}>
          <InputText
            name="name"
            title="Name"
            value={country?.name}
            placeholder=""
            disabled
          />
        </div>
        <div className={cn.countryFormGroup}>
          <InputText
            name="alpha3"
            title="Alpha 3"
            value={country?.alpha3}
            placeholder=""
            disabled
          />
        </div>
        <div className={cn.countryFormGroup}>
          <InputText
            name="alpha2"
            title="Alpha 2"
            value={country?.alpha2}
            placeholder=""
            disabled
          />
        </div>
        <div className={cn.countryFormGroup}>
          <InputText
            name="numeric"
            title="ISO"
            value={country?.numeric}
            placeholder=""
            disabled
          />
        </div>
        <div className={cn.countryFormGroup}>
          <InputText
            name="riskScore"
            title="Risk Score"
            value={riskScoreValue}
            placeholder=""
            onChange={(e) => setRiskScoreValue(e.target.value)}
          />
        </div>
      </>
    );
  }

  return (
    <SidePanel
      title="Country details"
      isOpen={panelIsOpen}
      type={SidePanelType.AML}
      onClose={handleClose}
      errorSidepanel={errorList}
      sidepanelExpandedKey="country-details"
      footer={
        <>
          <ButtonPrimary
            text={requestInProgress || activeInProgress ? 'Loading...' : 'Save'}
            onClick={updateCountryRiskScoreAction}
            disabled={
              requestInProgress || isLoading || isFetching || activeInProgress
            }
            bold
          />
          <ButtonPseudo text="Close" onClick={handleClose} bold />
        </>
      }
      closeOnClickOutside
    >
      {content}
    </SidePanel>
  );
};

export default CountryPanel;
